import {ChangeDetectionStrategy, Component, computed, effect, ElementRef, inject, input, signal, viewChild} from '@angular/core';
import {DatePipe, DecimalPipe, PercentPipe} from '@angular/common';
import {MAT_DIALOG_DATA, MatDialog, MatDialogRef} from '@angular/material/dialog';
import {MatProgressSpinnerModule} from '@angular/material/progress-spinner';
import {rxResource} from '@angular/core/rxjs-interop';
import {MatFormFieldModule} from '@angular/material/form-field';
import {MatSelectModule} from '@angular/material/select';
import {MatTableModule} from '@angular/material/table';
import {MatTabsModule} from '@angular/material/tabs';
import * as echarts from 'echarts';
import {County} from '../../../core/classes/county';
import {
  EdgarPipelineService,
  WeatherCounty,
  WeatherCountyDetail,
  WeatherCurve,
  WeatherEvent,
} from '../../../services/edgar-pipeline.service';
import {WEATHER_CATEGORY_COLORS} from '../../utilities/weather-category';

type WeatherPopupData = County & {
  weatherData: WeatherCounty | null;
  weatherMetricLabel: string;
  weatherMetricDisplayValue: string;
  selectedWeatherCategory: string;
};
type DetailTab = 'timeline' | 'summary' | 'event' | 'curves';

@Component({
  selector: 'tgrid-weather-outage-popup',
  imports: [
    DatePipe,
    DecimalPipe,
    PercentPipe,
    MatFormFieldModule,
    MatProgressSpinnerModule,
    MatSelectModule,
    MatTableModule,
    MatTabsModule,
  ],
  templateUrl: './weather-outage-popup.component.html',
  styleUrl: './weather-outage-popup.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WeatherOutagePopupComponent {
  private readonly pipeline = inject(EdgarPipelineService);
  private readonly dialog = inject(MatDialog);
  private readonly dialogData = inject<WeatherPopupData | null>(MAT_DIALOG_DATA, {optional: true});
  private readonly dialogRef = inject<MatDialogRef<WeatherOutagePopupComponent> | null>(MatDialogRef, {optional: true});
  public readonly data = input<WeatherPopupData | null>(null);
  protected readonly sourceData = computed(() => this.dialogData ?? this.data());
  protected readonly isDetailed = computed(() => this.dialogData !== null);
  protected readonly visible = signal(true);
  private readonly detailResource = rxResource({
    request: () => this.isDetailed() ? this.sourceData()?.weatherData?.fips : undefined,
    loader: ({request}) => this.pipeline.getWeatherCounty(request as string),
  });
  protected readonly detail = computed(() => this.detailResource.value() ?? null);
  protected readonly loading = computed(() => this.detailResource.isLoading());
  protected readonly tab = signal<DetailTab>('timeline');
  protected readonly weatherFilter = signal('All');
  protected readonly timelineYear = signal<number | null>(null);
  protected readonly selectedCurveCategories = signal<string[]>([]);
  protected readonly selectedEventId = signal('');
  protected readonly tabIndex = computed(() => ['timeline', 'summary', 'event', 'curves'].indexOf(this.tab()));
  protected readonly curveChartElement = viewChild<ElementRef<HTMLDivElement>>('curveChart');
  protected readonly eventChartElement = viewChild<ElementRef<HTMLDivElement>>('eventChart');
  protected readonly historyChartElement = viewChild<ElementRef<HTMLDivElement>>('historyChart');
  protected readonly exactTimelineChartElement = viewChild<ElementRef<HTMLDivElement>>('exactTimelineChart');
  protected readonly title = computed(() => {
    const source = this.sourceData();
    const weather = source?.weatherData;
    return weather ? `${weather.county}, ${weather.state}` : `${source?.name ?? 'County'}, ${source?.stateName ?? ''}`;
  });
  protected readonly associatedWeather = computed(() => {
    const summary = this.sourceData()?.weatherData;
    return summary?.outageAssociatedCategory
      ? `${summary.outageAssociatedCategory} (${summary.outageAssociatedCategoryEventCount.toLocaleString()} events)`
      : 'No identified association';
  });
  protected readonly availableCategories = computed(() => (this.detail()?.categories ?? []).map(row => row.category));
  protected readonly filteredEvents = computed(() => {
    const filter = this.weatherFilter();
    return (this.detail()?.events ?? []).filter(event => filter === 'All' || event.category === filter);
  });
  protected readonly filteredEpisodes = computed(() => {
    const filter = this.weatherFilter();
    return (this.detail()?.episodes ?? []).filter(episode => filter === 'All' || episode.category === filter || episode.categories.includes(filter));
  });
  protected readonly selectedMetrics = computed(() => {
    const detail = this.detail();
    if (!detail) return null;
    return this.weatherFilter() === 'All' ? detail.all : detail.categories.find(row => row.category === this.weatherFilter()) ?? null;
  });
  protected readonly visibleCurves = computed(() => {
    const selected = new Set(this.selectedCurveCategories());
    return (this.detail()?.curves ?? []).filter(curve => selected.has(curve.category));
  });
  protected readonly weatherSpecificCurveCount = computed(() => {
    const filter = this.weatherFilter();
    return (this.detail()?.curves ?? []).filter(curve =>
      curve.category !== 'All weather-associated' && (filter === 'All' || curve.category === filter)).length;
  });
  protected readonly selectedEvent = computed(() => {
    const events = this.filteredEvents();
    return events.find(event => event.eventId === this.selectedEventId()) ?? events[0] ?? null;
  });
  protected readonly selectedEpisode = computed(() => {
    const detail = this.detail();
    const episodeId = this.selectedEvent()?.weatherEpisodeId;
    return detail?.episodes.find(episode => episode.episodeId === episodeId) ?? null;
  });
  private curveChart?: echarts.ECharts;
  private eventChart?: echarts.ECharts;
  private historyChart?: echarts.ECharts;
  private exactTimelineChart?: echarts.ECharts;
  private initializedFips = '';

  public constructor() {
    this.weatherFilter.set(this.dialogData?.selectedWeatherCategory ?? 'All');
    effect(() => {
      const detail = this.detail();
      if (!detail || detail.fips === this.initializedFips) return;
      this.initializedFips = detail.fips;
      const preferred = this.weatherFilter() !== 'All'
        ? detail.curves.find(curve => curve.category === this.weatherFilter())
        : detail.curves.find(curve => curve.category === 'All weather-associated')
        ?? detail.curves.find(curve => curve.category === detail.dominantCategory)
        ?? detail.curves[0];
      this.selectedCurveCategories.set(preferred ? [preferred.category] : []);
      const preferredEvent = [...detail.events].filter(event => Boolean(event.category))
        .sort((left, right) => right.start.valueOf() - left.start.valueOf())[0] ?? detail.events.at(-1);
      this.selectedEventId.set(preferredEvent?.eventId ?? '');
      this.timelineYear.set(detail.coverageYears.at(-1) ?? null);
    });
    effect(() => {
      const element = this.curveChartElement()?.nativeElement;
      const curves = this.visibleCurves();
      if (!element || curves.length === 0) return;
      if (this.curveChart?.getDom() !== element) {
        this.curveChart?.dispose();
        this.curveChart = echarts.init(element);
      }
      this.curveChart.setOption(this.curveOptions(curves), true);
    });
    effect(() => {
      const element = this.eventChartElement()?.nativeElement;
      const event = this.selectedEvent();
      if (!element || !event || event.observations.length < 2) return;
      if (this.eventChart?.getDom() !== element) {
        this.eventChart?.dispose();
        this.eventChart = echarts.init(element);
      }
      this.eventChart.setOption(this.eventOptions(event), true);
    });
    effect(() => {
      const element = this.historyChartElement()?.nativeElement;
      const detail = this.detail();
      if (!element || !detail) return;
      if (this.historyChart?.getDom() !== element) {
        this.historyChart?.dispose();
        this.historyChart = echarts.init(element);
      }
      this.historyChart.setOption(this.monthlyHistoryOptions(detail), true);
    });
    effect(() => {
      const element = this.exactTimelineChartElement()?.nativeElement;
      const detail = this.detail();
      const year = this.timelineYear();
      this.weatherFilter();
      if (!element || !detail || year === null) return;
      if (this.exactTimelineChart?.getDom() !== element) {
        this.exactTimelineChart?.dispose();
        this.exactTimelineChart = echarts.init(element);
      }
      this.exactTimelineChart.setOption(this.exactTimelineOptions(detail, year), true);
    });
  }

  protected openDetails(): void {
    const source = this.sourceData();
    if (!source?.weatherData) return;
    this.visible.set(false);
    this.dialog.open(WeatherOutagePopupComponent, {
      data: source,
      autoFocus: false,
      restoreFocus: false,
      width: 'calc(100vw - min(570px, 44vw) - 32px)',
      maxWidth: 'none',
      height: 'calc(100vh - 128px)',
      maxHeight: 'calc(100vh - 128px)',
      position: {top: '112px', right: '16px'},
      hasBackdrop: false,
      panelClass: 'weather-outage-detail-dialog',
    });
  }

  protected closeSummary(): void {
    this.visible.set(false);
  }

  protected closeDetails(): void {
    this.dialogRef?.close();
  }

  protected updateTimelineYear(year: number): void {
    this.timelineYear.set(year);
  }

  protected supportLabel(support: string | null | undefined): string {
    if (!support) return 'N/A';
    switch (support) {
      case 'ADEQUATE_SUPPORT': return 'Adequate support';
      case 'LOW_SUPPORT_LT5_EPISODES': return 'Fewer than 5 weather episodes';
      case 'LOW_SUPPORT_LT5_EVENTS': return 'Fewer than 5 associated outage events';
      case 'NO_ASSOCIATED_EVENTS': return 'No associated outage events';
      default: return support.replaceAll('_', ' ').toLowerCase();
    }
  }

  protected setTab(tab: DetailTab): void {
    this.tab.set(tab);
  }

  protected setTabByIndex(index: number): void {
    const tab = ['timeline', 'summary', 'event', 'curves'][index] as DetailTab | undefined;
    if (tab) this.setTab(tab);
  }

  protected selectEvent(eventId: string): void {
    this.selectedEventId.set(eventId);
  }

  protected updateWeatherFilter(filter: string): void {
    this.weatherFilter.set(filter);
    this.selectedEventId.set('');
    if (filter !== 'All' && this.detail()?.curves.some(curve => curve.category === filter)) this.selectedCurveCategories.set([filter]);
  }

  protected openHistoryEvent(eventId: string): void {
    this.selectedEventId.set(eventId);
    this.tab.set('event');
  }

  protected toggleCurve(category: string, event: Event): void {
    const checked = (event.target as HTMLInputElement).checked;
    const selected = this.selectedCurveCategories();
    this.selectedCurveCategories.set(checked ? [...selected, category] : selected.filter(value => value !== category));
  }

  protected categoryColor(category: string | null): string {
    if (category === 'All weather-associated') return '#17211d';
    return category ? WEATHER_CATEGORY_COLORS[category] ?? '#8d8374' : '#d9d9d4';
  }

  protected associationLabel(event: WeatherEvent): string {
    if (!event.associationBasis) return 'No identified association';
    if (event.associationBasis === 'escalation_overlap') return `${event.escalationOverlapHours ?? 0} h escalation overlap`;
    return event.associationGapHours == null ? event.associationBasis.replaceAll('_', ' ') : `${event.associationGapHours} h preceding gap`;
  }

  private curveOptions(curves: WeatherCurve[]): echarts.EChartsOption {
    return {
      animation: false,
      color: curves.map(curve => this.categoryColor(curve.category)),
      grid: {top: 32, right: 20, bottom: 48, left: 64},
      legend: {type: 'scroll', top: 0},
      tooltip: {trigger: 'axis', valueFormatter: value => `${(Number(value) * 100).toFixed(2)}%`},
      xAxis: {type: 'value', name: 'Minutes from outage peak', nameLocation: 'middle', nameGap: 30},
      yAxis: {type: 'value', min: 0, name: 'Outage fraction', axisLabel: {formatter: (value: number) => `${(value * 100).toFixed(0)}%`}},
      series: curves.map(curve => ({
        type: 'line',
        name: `${curve.category} · n=${curve.supportingEvents}`,
        data: curve.points
          .filter(point => Number.isFinite(point.timeMinutes) && Number.isFinite(point.outageFraction))
          .map(point => [point.timeMinutes, point.outageFraction]),
        showSymbol: false,
        lineStyle: {
          color: this.categoryColor(curve.category),
          width: curve.category === 'All weather-associated' ? 3 : 2,
          type: curve.support === 'ADEQUATE_SUPPORT' ? 'solid' : 'dashed',
        },
        markLine: {silent: true, symbol: 'none', label: {show: false}, data: [{xAxis: 0, lineStyle: {color: '#64748b', type: 'dashed'}}]},
      })),
    };
  }

  private eventOptions(event: WeatherEvent): echarts.EChartsOption {
    const episode = this.selectedEpisode();
    const markArea = episode ? [[
      {name: episode.profile, xAxis: episode.start.valueOf(), itemStyle: {color: this.categoryColor(event.category), opacity: .1}},
      {xAxis: episode.end.valueOf()},
    ]] : [];
    return {
      animation: false,
      grid: {top: 64, right: 24, bottom: 58, left: 72},
      tooltip: {trigger: 'axis', valueFormatter: value => `${Number(value).toLocaleString()} customers`},
      xAxis: {type: 'time', name: 'Observation time', nameLocation: 'middle', nameGap: 38, axisLabel: {hideOverlap: true}},
      yAxis: {type: 'value', min: 0, name: 'Customers without power'},
      series: [{
        type: 'line', name: 'Observed customers without power',
        data: event.observations.map(observation => [observation.timestamp.valueOf(), observation.customersWithoutPower]),
        showSymbol: true, symbolSize: 3.6, lineStyle: {color: '#2f6f98', width: 2.5}, itemStyle: {color: '#2f6f98'},
        markArea: {silent: true, label: {show: false}, data: markArea as never},
        markLine: {
          silent: true, symbol: 'none', label: {show: true, rotate: 0, position: 'insideEndTop', fontSize: 9},
          data: [
            {name: 'Low-threshold start', xAxis: event.start.valueOf(), lineStyle: {color: '#5f7182', type: 'dashed'}},
            {name: 'High-threshold trigger', xAxis: event.high.valueOf(), lineStyle: {color: '#5f7182', type: 'dashed'}},
            {name: 'First observed maximum', xAxis: event.peakTime.valueOf(), lineStyle: {color: '#8b3f2f', width: 1.8}},
            {name: 'Low-threshold end', xAxis: event.end.valueOf(), lineStyle: {color: '#5f7182', type: 'dashed'}},
          ],
        },
      }],
    };
  }

  private monthlyHistoryOptions(detail: WeatherCountyDetail): echarts.EChartsOption {
    const filter = this.weatherFilter();
    const months = detail.coverageYears.flatMap(year => Array.from({length: 12}, (_unused, index) =>
      `${year}-${String(index + 1).padStart(2, '0')}`));
    const episodes = detail.episodes.filter(episode => filter === 'All' || episode.category === filter);
    const events = detail.events.filter(event => filter === 'All' || event.category === filter);
    const categories = [...new Set(episodes.map(episode => episode.category))]
      .sort((left, right) => episodes.filter(item => item.category === right).length - episodes.filter(item => item.category === left).length);
    const episodeCounts = new Map(categories.map(category => [category, new Array<number>(months.length).fill(0)]));
    const associatedEvents = new Array<number>(months.length).fill(0);
    const otherEvents = new Array<number>(months.length).fill(0);
    for (const episode of episodes) {
      const index = months.indexOf(episode.start.format('YYYY-MM'));
      if (index >= 0) (episodeCounts.get(episode.category) as number[])[index] += 1;
    }
    for (const event of events) {
      const index = months.indexOf(event.start.format('YYYY-MM'));
      if (index >= 0) (event.category ? associatedEvents : otherEvents)[index] += 1;
    }
    return {
      animation: false,
      color: [...categories.map(category => this.categoryColor(category)), '#1d6650', '#a7aaa4'],
      grid: [
        {top: 42, right: 18, height: 180, left: 58},
        {top: 278, right: 18, height: 90, left: 58},
      ],
      legend: {type: 'scroll', bottom: 0, left: 58, right: 18},
      tooltip: {trigger: 'axis'},
      xAxis: [
        {type: 'category', gridIndex: 0, data: months, axisLabel: {show: false}, axisTick: {show: false}},
        {type: 'category', gridIndex: 1, data: months, axisLabel: {interval: 11, formatter: (value: string) => value.slice(0, 4)}, axisTick: {show: false}},
      ],
      yAxis: [
        {type: 'value', gridIndex: 0, name: 'Weather episode starts', minInterval: 1},
        {type: 'value', gridIndex: 1, name: 'Outage event starts', minInterval: 1},
      ],
      series: [
        ...categories.map(category => ({
          type: 'bar' as const, name: category, stack: 'weather-episodes', xAxisIndex: 0, yAxisIndex: 0,
          data: episodeCounts.get(category), barMaxWidth: 11, itemStyle: {color: this.categoryColor(category)},
        })),
        {type: 'bar', name: 'Weather-associated outage', stack: 'outages', xAxisIndex: 1, yAxisIndex: 1, data: associatedEvents, barMaxWidth: 11, itemStyle: {color: '#1d6650'}},
        {type: 'bar', name: 'Other outage', stack: 'outages', xAxisIndex: 1, yAxisIndex: 1, data: otherEvents, barMaxWidth: 11, itemStyle: {color: '#a7aaa4'}},
      ],
    };
  }

  private exactTimelineOptions(detail: WeatherCountyDetail, year: number): echarts.EChartsOption {
    const start = Date.UTC(year, 0, 1);
    const end = Date.UTC(year + 1, 0, 1);
    const filter = this.weatherFilter();
    const episodes = detail.episodes.filter(episode => {
      const episodeStart = episode.start.valueOf();
      const episodeEnd = episode.end.valueOf();
      return (filter === 'All' || episode.category === filter) && episodeStart < end && episodeEnd >= start;
    });
    const events = detail.events.filter(event => {
      const eventStart = event.start.valueOf();
      return eventStart >= start && eventStart < end && (filter === 'All' || event.category === filter);
    });
    const categories = [...new Set(episodes.map(episode => episode.category))].sort();
    if (events.some(event => event.category === null)) categories.push('Unassociated outage');
    const categoryIndex = new Map(categories.map((category, index) => [category, index]));
    return {
      animation: false,
      grid: {top: 18, right: 24, bottom: 52, left: 135},
      tooltip: {trigger: 'item'},
      xAxis: {type: 'time', min: start, max: end, axisLabel: {formatter: '{MMM}'}, splitLine: {show: true, lineStyle: {type: 'dashed'}}},
      yAxis: {type: 'category', data: categories, axisLabel: {width: 115, overflow: 'truncate'}},
      series: [
        {
          type: 'custom', name: 'Weather episodes', silent: true,
          data: episodes.map(episode => [episode.start.valueOf(), episode.end.valueOf(), categoryIndex.get(episode.category) ?? 0, episode.category, episode.associatedEvents]),
          renderItem: (_params, api) => {
            const startPoint = api.coord([api.value(0), api.value(2)]);
            const endPoint = api.coord([api.value(1), api.value(2)]);
            const category = String(api.value(3));
            return {
              type: 'rect',
              shape: {x: startPoint[0], y: startPoint[1] - 5, width: Math.max(endPoint[0] - startPoint[0], 2), height: 10, r: 3},
              style: {fill: this.categoryColor(category), opacity: Number(api.value(4)) > 0 ? .95 : .55},
            };
          },
        },
        {
          type: 'scatter', name: 'Outage first maximum',
          data: events.map(event => ({
            value: [event.peakTime.valueOf(), categoryIndex.get(event.category ?? 'Unassociated outage') ?? 0, event.peakFraction ?? 0],
            itemStyle: {color: this.categoryColor(event.category)},
          })),
          symbolSize: value => Math.max(7, Math.min(16, 7 + Number((value as number[])[2]) * 8)),
          itemStyle: {borderColor: '#fff', borderWidth: 1.2},
        },
      ],
    };
  }
}
